// Single source of truth for the DSL op surface.
//
// The canonical scope graph (every `opMeta` op group) and the one walk
// that pairs each registered schema with its `BaseOpMeta`. Both the
// LLM-facing `<ExprDslReference>` renderer and the `op-examples`
// round-trip guard consume these — neither re-declares the scope nor
// re-implements the walk.
//
// Lives here (not in `op-meta.ts`) because composing the canonical scope
// requires importing `builtins` + every `ops/*` group, and `op-meta.ts`
// is a dependency leaf those modules import from — putting it there would
// create an import cycle.

import { builtins } from './builtins/index.ts';
import {
  asOpSchema,
  type BaseOpMeta,
  collectScopes,
  createOpTable,
  defineScope,
  type NameTable,
  type OpNameOf,
  opMeta,
  opNameOf,
  type Scope,
} from './op-meta.ts';
import { dashboardOps } from './ops/dashboard/index.ts';
import { transformOps } from './ops/transform/index.ts';
import { providerOps } from './providers/query-op.ts';

/**
 * The canonical top-level op schemas: every op group the engine ships
 * with, in deterministic group order. Used both as the schema list for
 * {@link canonicalDslScope} (doc walker root) and as the schema source
 * for {@link canonicalOpTable} (dispatch table) — one declaration site
 * for "every op the engine knows about at top level".
 */
const canonicalOpSchemas = [...builtins, ...providerOps, ...transformOps, ...dashboardOps] as const;

/**
 * The canonical opcode union — every op name the engine ships with,
 * DERIVED at the type level from {@link canonicalOpSchemas}. Each op
 * schema declares its name as `z.literal('<name>')`; this extracts that
 * literal from every registered schema and unions them, so the set is
 * exactly the registered surface with no second hand-maintained list.
 *
 * `OpCall.name` is typed `Opcode`, so every compile-time `name === '<op>'`
 * guard and `op('<op>')` producer is checked against the canonical set.
 * Runtime dispatch authority stays with `resolveOpCall`/{@link canonicalOpTable};
 * this union only constrains the static guards, it does not close the dispatcher.
 */
export type Opcode = OpNameOf<(typeof canonicalOpSchemas)[number]>;

/**
 * Every opcode as a runtime-derived frozen set — the value-level companion
 * to {@link Opcode}, built from the same `canonicalOpSchemas` registry via
 * `opNameOf`. Membership tests over an arbitrary string narrow it to
 * `Opcode` without a hand-maintained list.
 */
export const OPS: ReadonlySet<Opcode> = new Set(
  canonicalOpSchemas.map((schema) => opNameOf(asOpSchema(schema)) as Opcode),
);

/**
 * Runtime guard: is `name` a registered opcode? Narrows `string` to
 * {@link Opcode} against the same registry-derived {@link OPS} set.
 */
export const isOpcode = (name: string): name is Opcode => OPS.has(name as Opcode);

/**
 * Canonical top-level scope: every top-level op (builtins +
 * provider source ops + transform + dashboard) in one section.
 * Agg-context / dashboard tiles are reached transitively via
 * `childScopes`.
 */
export const canonicalDslScope: Scope<unknown> = defineScope({
  name: 'Top-level ops',
  description:
    'Callable at script top level. The window/scan ops (the Fill / Shift / First-last / Rolling / Cumulative / Change groups below, plus `rolling-slope`, `ewma`, `cum-integral`) share frame kwargs: `:order-by` (default `timestamp`), `:partition-by [cols…]`, and `:as` naming the output column (default `<col>_<op>`); applied to a timeseries-ref column they operate per series, ordered by inner timestamp.',
  registry: opMeta,
  schemas: canonicalOpSchemas,
});

/**
 * The full set of roots the DSL surface is walked from. Single root in
 * the unified-registry world; agg-context is reached transitively via
 * `aggregate`/`time-bucket`'s `childScopes`.
 */
export const canonicalDslRoots: readonly Scope<unknown>[] = [canonicalDslScope];

/**
 * Canonical op dispatch table — composes every top-level op group the
 * engine ships with. SSOT for the "full surface" composition shared by
 * production (`plugin-chat`'s `CHAT_OPS_TABLE`) and test fixtures
 * (`testing/fixtures.ts`'s `evalSrcWith`). Adding a new op group is a
 * single edit at {@link canonicalOpSchemas}.
 */
export const canonicalOpTable: NameTable = createOpTable([...canonicalOpSchemas]);

export type OpMetaEntry = { readonly op: ReturnType<typeof asOpSchema>; readonly meta: BaseOpMeta | undefined };
export type ScopeOps = { readonly scope: Scope<unknown>; readonly entries: readonly OpMetaEntry[] };

/**
 * Collect every scope transitively reachable from `roots` (deduped by
 * scope identity, deterministic order via the shared `collectScopes`
 * DFS) and pair each registered schema with its `BaseOpMeta`. The
 * single home for the `as BaseOpMeta` registry-read cast.
 *
 * Cross-scope op de-duplication *by name* is intentionally NOT done
 * here — the example round-trip guard needs every op's example, while
 * the doc renderer collapses by name. Name-dedup is the consumer's
 * concern; this walk only dedups scopes by identity.
 */
export const walkOpMeta = (roots: readonly Scope<unknown>[] = canonicalDslRoots): readonly ScopeOps[] => {
  const walked = roots.flatMap((root) => collectScopes(root));
  const scopes = walked.filter((scope, i) => walked.indexOf(scope) === i);
  return scopes.map((scope) => ({
    scope,
    entries: scope.schemas.map((sch) => {
      const op = asOpSchema(sch);
      return { op, meta: scope.registry.get(op) as BaseOpMeta | undefined };
    }),
  }));
};
